// RoomQueue.jsx — Shared queue panel
// Props: queue, currentIndex, isHost, username, socket, roomId,
//        onChangeTrack, onAddMoment (optional: opens a search/moment picker)

import { useState } from 'react';

export default function RoomQueue({
  queue = [],
  currentIndex = 0,
  isHost = false,
  username,
  socket,
  roomId,
  onChangeTrack,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [hovered, setHovered] = useState(null);

  const handleRemove = (e, index) => {
    e.stopPropagation();
    if (!socket) return;
    socket.emit('room_queue_remove', { roomId, index });
  };

  const canRemove = (item, index) =>
    isHost || item.addedBy === username;

  return (
    <div style={styles.wrapper}>
      <style>{`
        @keyframes rq-fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .rq-item { transition: background 0.15s; }
        .rq-item:hover { background: rgba(212,83,126,0.07) !important; }
        .rq-remove { transition: opacity 0.15s, transform 0.1s; }
        .rq-remove:hover { opacity: 1 !important; transform: scale(1.15); }
        .rq-toggle { transition: color 0.15s; }
        .rq-toggle:hover { color: #D4537E !important; }
      `}</style>

      {/* Header */}
      <button
        className="rq-toggle"
        onClick={() => setCollapsed(v => !v)}
        style={styles.toggleBtn}
      >
        <div style={styles.toggleLeft}>
          <span style={styles.toggleIcon}>{collapsed ? '▶' : '▼'}</span>
          <span style={styles.toggleLabel}>Queue</span>
          <span style={styles.queueCount}>{queue.length} moment{queue.length !== 1 ? 's' : ''}</span>
        </div>
        {!collapsed && queue.length > 0 && (
          <div style={styles.progressPills}>
            {queue.slice(0, 8).map((_, i) => (
              <div
                key={i}
                style={{
                  ...styles.pill,
                  background: i === currentIndex
                    ? '#D4537E'
                    : i < currentIndex
                      ? 'rgba(212,83,126,0.25)'
                      : 'rgba(255,255,255,0.1)',
                }}
              />
            ))}
            {queue.length > 8 && (
              <span style={styles.pillMore}>+{queue.length - 8}</span>
            )}
          </div>
        )}
      </button>

      {/* Queue list */}
      {!collapsed && (
        <div style={styles.list}>
          {queue.length === 0 ? (
            <div style={styles.emptyWrap}>
              <div style={styles.emptyIcon}>♪</div>
              <p style={styles.emptyText}>Nothing queued yet</p>
              <p style={styles.emptySub}>Add moments from your feed</p>
            </div>
          ) : (
            queue.map((item, i) => {
              const isActive = i === currentIndex;
              const isUpNext = i === currentIndex + 1;
              return (
                <div
                  key={i}
                  className="rq-item"
                  onClick={() => isHost && onChangeTrack(i)}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    ...styles.item,
                    background: isActive
                      ? 'rgba(212,83,126,0.12)'
                      : 'transparent',
                    borderLeft: isActive
                      ? '2px solid #D4537E'
                      : '2px solid transparent',
                    cursor: isHost ? 'pointer' : 'default',
                    animation: `rq-fadeIn 0.2s ease-out ${i * 0.03}s both`,
                  }}
                >
                  {/* Index / playing indicator */}
                  <div style={styles.indexCol}>
                    {isActive ? (
                      <div style={styles.activeIndicator}>
                        <span style={styles.activeDot} />
                        <span style={styles.activeDot} />
                        <span style={styles.activeDot} />
                      </div>
                    ) : (
                      <span style={{
                        ...styles.indexNum,
                        color: i < currentIndex
                          ? 'rgba(245,230,237,0.15)'
                          : 'rgba(245,230,237,0.3)',
                      }}>
                        {i + 1}
                      </span>
                    )}
                  </div>

                  {/* Thumbnail */}
                  {item.album_image
                    ? <img src={item.album_image} alt="" style={{
                        ...styles.thumb,
                        opacity: i < currentIndex ? 0.4 : 1,
                      }} />
                    : <div style={{ ...styles.thumbFallback, opacity: i < currentIndex ? 0.4 : 1 }}>♪</div>
                  }

                  {/* Info */}
                  <div style={styles.itemInfo}>
                    <div style={{
                      ...styles.itemTrack,
                      color: isActive ? '#D4537E' : i < currentIndex ? 'rgba(245,230,237,0.3)' : '#f5e6ed',
                    }}>
                      {item.track_name}
                    </div>
                    <div style={styles.itemMeta}>
                      <span style={styles.itemArtist}>{item.artist_name}</span>
                      {item.addedBy && (
                        <span style={styles.itemAdded}>· @{item.addedBy}</span>
                      )}
                    </div>
                  </div>

                  {/* Right side badges + remove */}
                  <div style={styles.itemRight}>
                    {isActive && (
                      <span style={styles.nowBadge}>▶ Now</span>
                    )}
                    {isUpNext && !isActive && (
                      <span style={styles.nextBadge}>Next</span>
                    )}
                    {canRemove(item, i) && hovered === i && !isActive && (
                      <button
                        className="rq-remove"
                        onClick={(e) => handleRemove(e, i)}
                        style={styles.removeBtn}
                        title="Remove from queue"
                      >✕</button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: {
    background: 'rgba(255,255,255,0.02)',
    border: '0.5px solid rgba(212,83,126,0.12)',
    borderRadius: 14, overflow: 'hidden',
    marginTop: 12,
  },

  // Toggle header
  toggleBtn: {
    width: '100%', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 14px',
    background: 'none', border: 'none',
    cursor: 'pointer', color: 'rgba(245,230,237,0.45)',
  },
  toggleLeft: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  toggleIcon: {
    fontSize: 8, color: 'rgba(212,83,126,0.5)',
  },
  toggleLabel: {
    fontSize: 10, letterSpacing: '0.18em',
    textTransform: 'uppercase', color: 'rgba(212,83,126,0.65)',
    fontWeight: 600,
  },
  queueCount: {
    fontSize: 10, color: 'rgba(245,230,237,0.25)',
    letterSpacing: '0.04em',
  },
  progressPills: {
    display: 'flex', alignItems: 'center', gap: 3,
  },
  pill: {
    width: 14, height: 3, borderRadius: 2,
    transition: 'background 0.3s',
  },
  pillMore: {
    fontSize: 9, color: 'rgba(245,230,237,0.2)',
    marginLeft: 2,
  },

  // List
  list: {
    borderTop: '0.5px solid rgba(212,83,126,0.08)',
    maxHeight: 340, overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(212,83,126,0.15) transparent',
  },

  // Empty
  emptyWrap: {
    textAlign: 'center', padding: '30px 20px',
  },
  emptyIcon: {
    fontSize: 32, opacity: 0.15, color: '#f5e6ed', marginBottom: 8,
  },
  emptyText: {
    fontSize: 13, color: 'rgba(245,230,237,0.35)',
    fontFamily: "'Cormorant Garamond', serif", marginBottom: 4,
  },
  emptySub: {
    fontSize: 11, color: 'rgba(245,230,237,0.2)',
    letterSpacing: '0.06em',
  },

  // Item
  item: {
    display: 'flex', alignItems: 'center',
    gap: 10, padding: '9px 14px',
    borderBottom: '0.5px solid rgba(255,255,255,0.04)',
  },
  indexCol: {
    width: 18, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  indexNum: {
    fontSize: 10, fontFamily: 'monospace',
  },
  activeIndicator: {
    display: 'flex', gap: 2, alignItems: 'flex-end',
  },
  activeDot: {
    width: 2, borderRadius: 1,
    background: '#D4537E',
    display: 'inline-block',
    animation: 'none',
    // Heights are staggered via inline override if needed
    height: 8,
    opacity: 0.9,
  },
  thumb: {
    width: 34, height: 34, borderRadius: 6,
    objectFit: 'cover', flexShrink: 0,
  },
  thumbFallback: {
    width: 34, height: 34, borderRadius: 6,
    background: 'rgba(255,255,255,0.05)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, color: 'rgba(245,230,237,0.3)', flexShrink: 0,
  },
  itemInfo: {
    flex: 1, minWidth: 0,
  },
  itemTrack: {
    fontSize: 13, fontWeight: 400,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    marginBottom: 2, transition: 'color 0.2s',
  },
  itemMeta: {
    fontSize: 11, display: 'flex', gap: 4, alignItems: 'center',
  },
  itemArtist: {
    color: 'rgba(245,230,237,0.3)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    maxWidth: 100,
  },
  itemAdded: {
    color: 'rgba(212,83,126,0.45)', flexShrink: 0,
  },
  itemRight: {
    flexShrink: 0, display: 'flex',
    alignItems: 'center', gap: 6,
  },
  nowBadge: {
    fontSize: 9, color: '#D4537E',
    letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  nextBadge: {
    fontSize: 9, color: 'rgba(245,230,237,0.25)',
    letterSpacing: '0.06em', textTransform: 'uppercase',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 4, padding: '2px 5px',
  },
  removeBtn: {
    fontSize: 10, color: 'rgba(245,230,237,0.35)',
    background: 'rgba(255,255,255,0.05)',
    border: '0.5px solid rgba(255,255,255,0.08)',
    borderRadius: 5, padding: '2px 6px',
    cursor: 'pointer', opacity: 0.7,
  },
};
